import { type DriveFile } from '@/shared/DriveTypes';
import { sort } from './sort';
import { getId, getParentFolders, isAFolder, isAShortcut } from './utils';

type Props = {
  file: DriveFile | undefined;
  files: DriveFile[];
  wiki: DriveFile | undefined;
};

const getNextChild = (files: DriveFile[], file: DriveFile, wiki: DriveFile): DriveFile | undefined => {
  if (!file.parents) return undefined;
  const parents = getParentFolders(files, file.parents[0]);
  const parent = files.find((o) => getId(o) === file.parents?.[0]);
  const siblings = sort(files.filter((o) => o.parents?.[0] === parent?.parents?.[0]));
  const fileIndex = siblings.findIndex((o) => o.id === parent?.id);
  const nextSibling = siblings[fileIndex + 1];

  if (nextSibling) return nextSibling;

  // If there is no nextNode and no parent
  // This is a top level file that is the last item in the wiki
  if (!parent) return wiki;

  // Walk up the tree until we find a parent with a next child
  for (const parent of parents) {
    const children = files.filter((o) => o.parents?.[0] === parent.id);
    if (children?.some((o) => o.id === file.id)) {
      const nextChild = getNextChild(files, parent, wiki);
      if (nextChild) return nextChild;
    }
  }

  return undefined;
};

const getDeepestLastChild = ({ file, files }: { file: DriveFile; files: DriveFile[] }): DriveFile | undefined => {
  const children = sort(files.filter((o) => o.parents?.[0] === getId(file)));
  if (!children.length) return file;

  const duplicate = isAShortcut(file) && files.some((o) => o.id === getId(file));
  if (duplicate) return file;

  const lastChild = children.at(-1);
  if (isAFolder(lastChild)) {
    return getDeepestLastChild({ file: lastChild, files });
  }
  return lastChild;
};

export const getPrevNext = ({
  file,
  files,
  wiki,
}: Props): { prev: DriveFile | undefined; next: DriveFile | undefined } => {
  if (!file) return { prev: undefined, next: undefined };
  const isWiki = file.id === wiki?.id;
  if (files.length <= 1) {
    if (isWiki) return { prev: undefined, next: files[0] };
    return { prev: wiki, next: undefined };
  }
  const siblings = sort(files.filter((o) => o.parents?.[0] === (isWiki ? wiki?.id : file.parents?.[0])));
  const duplicate = isAShortcut(file) ? files.some((o) => o.id === getId(file)) : false;
  const children = duplicate ? [] : sort(files.filter((o) => o.parents?.[0] === getId(file)));
  const fileIndex = siblings.findIndex((o) => o.id === file.id);
  const prevSibling = fileIndex <= 0 && isWiki ? siblings.at(-1) : siblings[fileIndex - 1];
  const nextSibling = siblings[fileIndex + 1];
  const nextChild =
    isAFolder(file) && children.length
      ? children.at(0)
      : wiki && !nextSibling
        ? getNextChild(files, file, wiki)
        : undefined;
  const next = nextChild ?? nextSibling;

  if ((isWiki && prevSibling) || isAFolder(prevSibling)) {
    const lastChild = getDeepestLastChild({ file: prevSibling, files });
    return { prev: lastChild, next };
  }

  const prevSiblingChildren = prevSibling ? sort(files.filter((o) => o.parents?.[0] === prevSibling.id)) : [];
  const parent = files.find((o) => getId(o) === file?.parents?.[0]) ?? wiki;
  const prev = prevSiblingChildren.at(-1) ?? prevSibling ?? parent;
  if (prev?.id === next?.id) return { prev: undefined, next: undefined };

  return { prev, next };
};

export const getPrevNextSibling = ({
  file,
  files,
}: {
  file: DriveFile;
  files: DriveFile[];
}): { prev: DriveFile | undefined; next: DriveFile | undefined } => {
  if (files.length <= 1) return { prev: undefined, next: undefined };
  const siblings = sort(files.filter((o) => o.parents?.[0] === file.parents?.[0]));
  const fileIndex = siblings.findIndex((o) => o.id === file.id);
  const prevSibling = siblings[fileIndex - 1];
  const nextSibling = siblings[fileIndex + 1];
  return { prev: prevSibling, next: nextSibling };
};
